import type { CreateTrendRequest, Me, TrendDetail } from '@trendator/shared';
import { useEffect, useRef, useState } from 'react';
import { ApiError, api, moderationMessage } from '../api';
import { BackIcon, ChevronDim, CloseIcon, DuoIcon, Sparkle, TrendUp, UploadIcon } from '../components/icons';
import { useBackButton } from '../hooks';
import { rub } from '../format';
import { haptic } from '../telegram';
import { trendPrice } from './Catalog';
import { Loading } from './Status';
import type { InsufficientFunds } from './Create';

const OUTPUT_NOUN = { image: 'фото', video: 'видео', song: 'песни', stickers: 'стикеров' } as const;

/** Экран тренда — docs/design/Trend.dc.html. Дуо-тренд: сначала зовём друга, генерация — после его согласия. */
export function TrendScreen({
  me,
  idOrSlug,
  rank,
  onBack,
  onCreated,
  onInsufficient,
  onOpenTwin,
  onDuo,
}: {
  me: Me;
  idOrSlug: string;
  /** Место в «Тренды сегодня» (если пришли с главной). */
  rank?: number;
  onBack: () => void;
  onCreated: (generationId: string) => void;
  onInsufficient: (data: InsufficientFunds) => void;
  /** Перейти во вкладку «Двойник», чтобы его создать. */
  onOpenTwin?: () => void;
  /** Дуо: приглашение создано — экран «Позови друга». */
  onDuo?: (code: string) => void;
}) {
  useBackButton(onBack);
  const [t, setT] = useState<TrendDetail | null>(null);
  const [missing, setMissing] = useState(false);
  const [view, setView] = useState<'after' | 'before'>('after');
  const [photos, setPhotos] = useState<{ id: string; url: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [twinReady, setTwinReady] = useState<boolean | null>(null);
  const [face, setFace] = useState<'twin' | 'upload'>('upload');
  const [fields, setFields] = useState<Record<string, string>>({});
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api
      .trend(idOrSlug)
      .then(setT)
      .catch(() => setMissing(true));
  }, [idOrSlug]);

  useEffect(() => {
    api
      .twin()
      .then((tw) => {
        const ready = tw.status === 'ready';
        setTwinReady(ready);
        if (ready) setFace('twin');
      })
      .catch(() => setTwinReady(false));
  }, []);

  if (missing) {
    return (
      <main className="screen screen--hero">
        <div className="center" style={{ minHeight: 'calc(80 * var(--dvh))' }}>
          <h1>Тренд недоступен</h1>
          <p>Его выключили или ссылка устарела. Загляни в каталог — там много нового.</p>
          <button type="button" className="btn-accent" style={{ maxWidth: 280 }} onClick={onBack}>
            Назад
          </button>
        </div>
      </main>
    );
  }
  if (!t) return <Loading />;

  const need = t.photos;
  const free = !t.duo && t.allowFree && me.freeGenerationAvailable;
  const twinOn = t.twin && twinReady === true && face === 'twin';
  const photosOk = twinOn || photos.length >= need.min;
  const missingField = t.fields.find((f) => f.required && !(fields[f.id] ?? '').trim());
  const canCreate = photosOk && !missingField && !busy && !uploading;
  const hero = view === 'before' && t.beforeUrl ? t.beforeUrl : t.coverUrl;

  const pickFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    if (file.size > 10 * 1024 * 1024) {
      setError('Фото больше 10 МБ — выбери поменьше.');
      return;
    }
    setUploading(true);
    try {
      const up = await api.upload(file);
      setPhotos((p) => [...p, up].slice(-Math.max(1, need.max)));
    } catch (err) {
      setError(
        moderationMessage(err) ??
          (err instanceof ApiError && err.code === 'not_image'
          ? 'Нужна картинка: JPG, PNG или WebP.'
          : err instanceof ApiError && err.code === 'too_many_uploads'
            ? 'Слишком много фото за час — попробуй позже.'
            : 'Не получилось загрузить фото.'),
      );
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const create = async () => {
    if (!canCreate) return;
    haptic();
    setBusy(true);
    setError(null);
    const values = Object.fromEntries(t.fields.map((f) => [f.id, (fields[f.id] ?? '').trim()]));
    const request: CreateTrendRequest = {
      kind: 'trend',
      trendId: t.id,
      photoIds: twinOn ? [] : photos.map((p) => p.id),
      ...(twinOn ? { useTwin: true } : {}),
      ...(t.fields.length ? { fields: values } : {}),
    };
    try {
      if (t.duo) {
        const { kind: _k, ...duo } = request;
        const inv = await api.createDuo(duo);
        onDuo?.(inv.code);
        return;
      }
      const g = await api.createGeneration(request);
      onCreated(g.id);
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError && err.code === 'insufficient_funds') {
        onInsufficient({ request, balance: Number(err.data.balance ?? 0), price: Number(err.data.price ?? t.price) });
        return;
      }
      const code = err instanceof ApiError ? err.code : '';
      const blocked = moderationMessage(err);
      if (blocked) {
        // Детское фото в «взрослом» тренде — убираем его, пусть выберет другое.
        if (code === 'minor_not_allowed') setPhotos([]);
        setError(blocked);
        return;
      }
      setError(
        code === 'trend_not_found'
          ? 'Этот тренд только что выключили.'
          : code === 'banned'
            ? 'Генерации для аккаунта недоступны. Напиши в поддержку.'
            : code === 'generations_disabled'
              ? 'Генерации ещё подключаются — загляни чуть позже.'
              : code === 'twin_not_ready'
                ? 'Двойник ещё не готов — выбери фото.'
                : code === 'bad_fields'
                  ? String((err as ApiError).data.message ?? 'Проверь поля.')
                  : 'Не получилось запустить. Попробуй ещё раз.',
      );
    }
  };

  return (
    <main className="screen trend">
      <div
        className="trend__hero"
        style={hero ? { backgroundImage: `url("${hero}")`, backgroundSize: 'cover', backgroundPosition: 'center' } : { background: t.coverGradient }}
      >
        <div className="trend__top">
          <button type="button" className="glass icon-btn trend__glass" aria-label="Назад" onClick={onBack}>
            <BackIcon />
          </button>
        </div>
        <div className="trend__hero-bottom">
          {rank !== undefined && rank < 3 && t.popularity > 0 ? (
            <span className="trend__rank">
              <TrendUp />#{rank + 1} в трендах
            </span>
          ) : t.tags.includes('new') ? (
            <span className="trend__rank trend__rank--new">Новинка</span>
          ) : null}
          {t.beforeUrl ? (
            <div className="trend__toggle" role="group" aria-label="Показать">
              {(['before', 'after'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={view === v}
                  onClick={() => {
                    haptic('select');
                    setView(v);
                  }}
                >
                  {v === 'before' ? 'До' : 'После'}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className="trend__info">
        <h1 className="trend__title">{t.title}</h1>
        {t.description ? <p className="trend__desc">{t.description}</p> : null}
        <p className="trend__meta">
          {t.frames} {OUTPUT_NOUN[t.output]} · ≈ {t.etaSec < 60 ? `${t.etaSec} сек` : `${Math.round(t.etaSec / 60)} мин`}
          {t.qc ? ' · брак переделаем бесплатно' : ''}
        </p>
      </div>

      {need.max > 0 || t.twin ? (
        <div className="glass-list trend__faces">
          {t.twin ? (
            <>
              {twinReady ? (
                <label className="glass-list__row trend__face">
                  <span className="trend__face-ic trend__face-ic--twin" />
                  <span className="trend__face-text">
                    <span className="trend__face-name">Мой двойник</span>
                    <span className="trend__face-sub">Готов · ничего не нужно загружать</span>
                  </span>
                  <input
                    type="radio"
                    name="face"
                    checked={face === 'twin'}
                    onChange={() => {
                      haptic('select');
                      setFace('twin');
                    }}
                    aria-label="Мой двойник"
                  />
                </label>
              ) : (
                <button type="button" className="glass-list__row glass-list__row--btn trend__face" onClick={onOpenTwin} disabled={!onOpenTwin}>
                  <span className="trend__face-ic trend__face-ic--twin" />
                  <span className="trend__face-text">
                    <span className="trend__face-name">Мой двойник</span>
                    <span className="trend__face-sub">10 фото один раз — и больше ничего не грузишь</span>
                  </span>
                  <ChevronDim />
                </button>
              )}
              <div className="glass-list__sep" />
            </>
          ) : null}
          <div className={`glass-list__row trend__face${t.duo ? ' trend__face--duo' : ' trend__face--off'}`} aria-disabled={!t.duo}>
            <span className="trend__face-ic">
              <DuoIcon />
            </span>
            <span className="trend__face-text">
              <span className="trend__face-name">Вдвоём с другом</span>
              <span className="trend__face-sub">{t.duo ? 'Обязательно · друг сделает селфи и согласится' : 'Для дуо-трендов · друг подтвердит сам'}</span>
            </span>
            {t.duo ? <span className="trend__duo-tag">дуо</span> : null}
          </div>
          {need.max > 0 ? (
            <>
              <div className="glass-list__sep" />
              <div className={`trend__face${face === 'upload' || !twinOn ? ' trend__face--on' : ''}`}>
                <label className="glass-list__row">
                  <span className="trend__face-ic">
                    <UploadIcon />
                  </span>
                  <span className="trend__face-text">
                    <span className="trend__face-name">{need.max > 1 ? `Загрузить фото (до ${need.max})` : 'Загрузить фото'}</span>
                    <span className="trend__face-sub">{t.photoHint}</span>
                  </span>
                  <input
                    type="radio"
                    name="face"
                    checked={!twinOn}
                    onChange={() => {
                      haptic('select');
                      setFace('upload');
                    }}
                    aria-label="Загрузить фото"
                  />
                </label>
                {!twinOn ? (
                  <div className="trend__photos">
                    {photos.map((p) => (
                      <span key={p.id} className="ref-chip trend__photo">
                        <img src={p.url} alt="Твоё фото" />
                        <button type="button" aria-label="Убрать фото" onClick={() => setPhotos((s) => s.filter((x) => x.id !== p.id))}>
                          <CloseIcon size={14} />
                        </button>
                      </span>
                    ))}
                    {photos.length < need.max ? (
                      <button type="button" className="trend__add" disabled={uploading} onClick={() => fileRef.current?.click()} aria-label="Добавить фото">
                        {uploading ? <span className="spinner spinner--sm" /> : '+'}
                      </button>
                    ) : null}
                    <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => void pickFile(e.target.files?.[0])} />
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      {t.fields.length ? (
        <div className="glass-list trend__fields">
          {t.fields.map((f, i) => (
            <div key={f.id}>
              {i > 0 ? <div className="glass-list__sep trend__fields-sep" /> : null}
              <label className="trend__field">
                <span className="trend__field-label">
                  {f.label}
                  {f.required ? '' : ' · необязательно'}
                </span>
                <input
                  value={fields[f.id] ?? ''}
                  maxLength={f.maxLength}
                  placeholder={f.placeholder}
                  onChange={(e) => setFields((v) => ({ ...v, [f.id]: e.target.value }))}
                />
              </label>
            </div>
          ))}
        </div>
      ) : null}

      {error ? (
        <p className="error-text trend__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="paybar">
        <div className="paybar__info">
          <span className="paybar__credits">{t.duo ? rub(t.price) : trendPrice(t, me)}</span>
          <span className="paybar__sub">{free ? 'первая генерация' : t.duo ? 'не согласится — вернём' : 'ошибка — вернём деньги'}</span>
        </div>
        <button type="button" className="btn-accent paybar__btn" disabled={!canCreate} onClick={() => void create()}>
          <Sparkle />
          {busy ? 'Запускаем…' : !photosOk ? 'Добавь фото' : missingField ? `Заполни «${missingField.label}»` : t.duo ? 'Позвать друга' : 'Создать'}
        </button>
      </div>
    </main>
  );
}
